import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import loadable from '@loadable/component';
import { type FunctionComponent } from 'react';
import { useInView } from 'react-intersection-observer';
import { isStorybook } from '../../../helpers/storybook/storybook-component.helper';
import { type HomeSection } from '../../../types/home.types';
import { AnimatedSplash } from '../animated-splash/animated-splash.component';
import { HomeCarousel } from '../home-carousel/home-carousel.component';
import {
	isCarouselSection,
	isDynamicYieldSection,
	isTipsInspirationSection,
	isExclusiveDealsSection,
	isProjectsSection,
	isShopByLookSection,
	isBannerSection,
	isFeaturedBrands,
	isAnimatedSplashSection,
	isSplashSection,
	isSalesSection
} from '../home-common.helpers';
import { HomeDynamicYield } from '../home-dynamic-yield/home-dynamic-yield.component';
import { HomeExclusiveDealsCardOption } from '../home-exclusive-deals-card-option/home-exclusive-deals-card-option.component';
import { HomeSplash } from '../home-splash/home-splash.component';

const LoadableHomeTipsInspiration = loadable(
	() => import(/* webpackChunkName: "home-tips-inspiration" */ '../home-tips-inspiration/home-tips-inspiration.component'),
	{
		resolveComponent: ({ HomeTipsInspiration }) => HomeTipsInspiration
	}
);
const LoadableHomeProjects = loadable(() => import(/* webpackChunkName: "home-projects" */ '../projects/projects.component'), {
	resolveComponent: ({ Projects }) => Projects
});
const LoadableNewHomeShopByLook = loadable(
	() => import(/* webpackChunkName: "new-home-shop-by-look" */ '../shop-by-look/shop-by-look/shop-by-look.component'),
	{
		resolveComponent: ({ HomeShopByLook }) => HomeShopByLook
	}
);
const LoadableHomeBanner = loadable(() => import(/* webpackChunkName: "home-banner" */ '../home-banner/home-banner.component'), {
	resolveComponent: ({ HomeBanner }) => HomeBanner
});
const LoadableFeaturedBrands = loadable(
	() => import(/* webpackChunkName: "home-feature-brands" */ '../featured-brands/featured-brands.component'),
	{
		resolveComponent: ({ FeaturedBrands }) => FeaturedBrands
	}
);

export type GenerateInViewHomeSectionProps = HomeSection;

export const GenerateInViewHomeSection: FunctionComponent<GenerateInViewHomeSectionProps> = ({ ...props }) => {
	const { ref, inView } = useInView({ triggerOnce: true });
	/* do NOT use intesectionObserver in the Storybook environment, this was causing snap issues for the homepage story */
	const shouldRender = inView || isStorybook();
	return <div ref={ref}>{shouldRender && <HomeSectionFactory {...props} />}</div>;
};

const HomeSectionFactory: FunctionComponent<HomeSection> = (props) => {
	if (isCarouselSection(props)) {
		// using the carousel title to identify the carousel
		const carouselTitle = kebabCase(props.title);
		return <HomeCarousel {...props} automationHook={`${carouselTitle}-swipe`} />;
	} else if (isDynamicYieldSection(props)) {
		return <HomeDynamicYield {...props} automationHook={props.name} />;
	} else if (isAnimatedSplashSection(props)) {
		const { animatedSplashImage } = props;
		return <AnimatedSplash {...props} image={animatedSplashImage} />;
	} else if (isSplashSection(props)) {
		return <HomeSplash {...props} />;
	} else if (isSalesSection(props)) {
		const { items = [] } = props;
		return <HomeCarousel items={items} automationHook="carousel-top-category-swipe" navigationVerticalPosition={-40} />;
	} else if (isTipsInspirationSection(props)) {
		return <LoadableHomeTipsInspiration {...props} />;
	} else if (isExclusiveDealsSection(props)) {
		return <HomeCarousel CardComponent={HomeExclusiveDealsCardOption} {...props} />;
	} else if (isProjectsSection(props)) {
		return <LoadableHomeProjects {...props} automationHook="project-tool-room-list" />;
	} else if (isShopByLookSection(props)) {
		return <LoadableNewHomeShopByLook {...props} />;
	} else if (isBannerSection(props)) {
		return <LoadableHomeBanner {...props} />;
	} else if (isFeaturedBrands(props)) {
		return <LoadableFeaturedBrands {...props} />;
	} else {
		return null;
	}
};
